import type { ActivityGeneration, Model } from "./index.js";

export type ComposerActivity = "ask" | ActivityGeneration;

export type ActivityCapableModel = Pick<
  Model,
  "remoteId" | "architecture" | "remoteMetadata" | "capabilities"
>;

function object(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function architectureModalities(architecture: Record<string, unknown> | null) {
  const parsed =
    typeof architecture?.modality === "string"
      ? architecture.modality.split("->", 2).map((part) =>
          part
            .split(/[+,]/)
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean),
        )
      : [];
  const input = strings(architecture?.input_modalities).map((value) =>
    value.toLowerCase(),
  );
  const output = strings(architecture?.output_modalities).map((value) =>
    value.toLowerCase(),
  );
  return {
    input: input.length ? input : (parsed[0] ?? []),
    output: output.length ? output : (parsed[1] ?? []),
  };
}

/**
 * Speech-to-text models advertise their output as "transcription" (OpenRouter,
 * OpenAI) rather than "text", while audio-capable chat models accept audio but
 * are not transcription endpoints. Treat a model as transcription-capable only
 * when it takes audio input and either declares a transcription output or is
 * clearly an STT model by id, so chat models never surface in the mode.
 */
function isTranscriptionCapable(
  model: ActivityCapableModel,
  input: string[],
  output: string[],
) {
  return (
    input.includes("audio") &&
    (output.includes("transcription") ||
      /(whisper|transcri|speech\s*[-.]?\s*to\s*[-.]?\s*text|\bstt\b|\basr\b)/i.test(
        model.remoteId,
      ))
  );
}

/** Shared source of truth for which models can drive each composer activity. */
export function supportsActivity(
  model: ActivityCapableModel,
  activity: ComposerActivity,
): boolean {
  const architecture = object(model.architecture);
  const { input, output } = architectureModalities(architecture);
  const remoteMetadata = object(model.remoteMetadata);
  const assetTypes = strings(
    remoteMetadata?.asset_types ?? remoteMetadata?.assetTypes,
  );
  if (activity === "model3d")
    return (
      assetTypes.includes("model3d") ||
      output.some((value) =>
        ["3d", "model3d", "mesh"].includes(value.toLowerCase()),
      )
    );
  if (activity === "sprite" || activity === "tileset")
    return assetTypes.includes(activity);
  if (activity === "image") return model.capabilities.imageGeneration;
  if (activity === "video") return output.includes("video");
  if (activity === "transcription")
    return isTranscriptionCapable(model, input, output);
  if (activity === "speech") {
    const voices =
      remoteMetadata?.supported_voices ?? remoteMetadata?.supportedVoices;
    return (
      (output.includes("audio") || output.includes("speech")) &&
      input.includes("text") &&
      ((Array.isArray(voices) && voices.length > 0) ||
        /tts|speech|eleven/i.test(model.remoteId))
    );
  }
  if (activity === "audio")
    return (
      output.includes("audio") &&
      input.includes("text") &&
      !supportsActivity(model, "speech")
    );
  return (
    (output.length === 0 || output.includes("text")) &&
    !model.capabilities.imageGeneration &&
    !output.includes("video")
  );
}
